import moment from 'moment'
import { HTMLAttributes, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { Skeleton } from '@/components/Skeleton'
import useSearchParams from '@/hooks/useSearchParams'
import { useLoadAnalyticsById } from '@/queries/form-analytics/useLoadAnalyticsById'
import Ui from '@/ui'

import { getDateRangeByPeriod } from '../../UtilsFormsAnalytics/getDateRangeByPeriod'
import { FormsAnalyticsCard } from '../FormsAnalyticsCard'

const CHART_OPTIONS = [
  { id: 'Starts', title: 'Starts' },
  { id: 'Completions', title: 'Completions' },
  { id: 'Abandonments', title: 'Abandonments' },
  { id: 'Completion Rate', title: 'Completion Rate' },
]

export const CompletionChart = (props: HTMLAttributes<HTMLDivElement>) => {
  const { resourceId } = useParams<{ resourceId: string }>()
  const searchParams = useSearchParams()

  const period = searchParams.get('period') || 'month'
  const startDate = searchParams.get('start_date')
  const endDate = searchParams.get('end_date')

  const { created_before, created_after } = getDateRangeByPeriod(
    period,
    startDate,
    endDate
  )

  const { data, isLoading } = useLoadAnalyticsById<'timeline'>({
    form: resourceId,
    created_after,
    created_before,
    method: 'timeline',
  })

  const [selectedChart, setSelectedChart] = useState('Completions')

  const formattedData = data?.map((day) => ({
    name: moment(day.date).format('MM/DD/YYYY'),
    Starts: day.total,
    Completions: day.completions,
    Abandonments: day.abandonments,
    'Completion Rate': (day.completions_rate || 0) / 100,
  }))

  const maxValue = useMemo(() => {
    if (!formattedData) return 0

    const maxStarts = Math.max(...formattedData.map((item) => item.Starts))
    const maxCompletions = Math.max(
      ...formattedData.map((item) => item.Completions)
    )
    const maxAbandonments = Math.max(
      ...formattedData.map((item) => item.Abandonments)
    )

    return Math.max(maxStarts, maxCompletions, maxAbandonments) + 5
  }, [formattedData])

  const yAxisTicks = useMemo(() => {
    if (selectedChart === 'Completion Rate') return undefined

    const ticks = []
    for (let i = 0; i <= maxValue; i += 10) {
      ticks.push(i)
    }
    return ticks
  }, [maxValue, selectedChart])

  const lineColors = {
    Starts: {
      active: '#3182CE',
    },
    Completions: {
      active: '#38A169',
    },
    Abandonments: {
      active: '#E53E3E',
    },
    'Completion Rate': {
      active: '#805AD5',
    },
  }

  return (
    <FormsAnalyticsCard.Root {...props}>
      <div className="mb-4 mt-1 px-4">
        <Ui.Tabs
          className="w-full [&>button]:!w-full"
          initialSelectedIndex={CHART_OPTIONS.findIndex(
            (opt) => opt.id === selectedChart
          )}
          onSelectOption={({ id }: { id: string }) => setSelectedChart(id)}
          options={CHART_OPTIONS}
        />
      </div>
      <Skeleton {...{ isLoading }} className="!h-80">
        <div className="h-80">
          <ResponsiveContainer height="100%" width="100%">
            <LineChart
              data={formattedData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                height={30}
                interval={0}
                tick={{ fontSize: 12 }}
                tickMargin={9}
              />
              <YAxis
                domain={
                  selectedChart === 'Completion Rate' ? [0, 1] : [0, maxValue]
                }
                tick={{ fontSize: 12 }}
                tickFormatter={
                  selectedChart === 'Completion Rate'
                    ? (value: number) =>
                        value.toLocaleString('en-US', {
                          style: 'percent',
                          minimumFractionDigits: 0,
                        })
                    : undefined
                }
                ticks={yAxisTicks}
                yAxisId="left"
              />
              <Tooltip
                formatter={(value: number, name: string) => {
                  if (name === 'Completion Rate') {
                    return [
                      value.toLocaleString('en-US', {
                        style: 'percent',
                        minimumFractionDigits: 0,
                      }),
                      name,
                    ]
                  }
                  return [value, name]
                }}
              />

              <Line
                activeDot={{ r: 8 }}
                dataKey={selectedChart}
                stroke={
                  lineColors[selectedChart as keyof typeof lineColors].active
                }
                strokeWidth={2}
                type="monotone"
                yAxisId="left"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Skeleton>
    </FormsAnalyticsCard.Root>
  )
}
